import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge      from "@react/shared/ui/Badge"
import EmptyState from "@react/shared/ui/EmptyState"

const GRID_COLUMNS = "140px minmax(220px, 1.7fr) minmax(180px, 1fr) 200px 140px"

const STATUS_TONE = {
  draft:    "neutral",
  active:   "success",
  inactive: "warning",
}

const SortHeader = ({ label, column, order, direction, onSortChange, align = "left" }) => {
  const active        = order === column
  const nextDirection = active && direction === "asc" ? "desc" : "asc"
  const justify       = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"
  return (
    <button
      type      = "button"
      onClick   = {() => onSortChange(column, nextDirection)}
      className = {`flex items-center gap-1 ${justify} uppercase tracking-wider text-[11px] font-medium ${active ? "text-slate-700" : "text-slate-500 hover:text-slate-700"}`}
    >
      {label}
      {active && <FontAwesomeIcon icon={direction === "asc" ? "arrow-up" : "arrow-down"} className="text-[9px]" />}
    </button>
  )
}

const ContractRow = ({ contract }) => (
  <div
    role      = "row"
    className = "group grid items-center gap-x-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
    onClick   = {() => { window.location.href = contract.show_url }}
  >
    <div>
      <Badge tone="neutral" variant="soft" size="xs" className="font-mono max-w-full truncate">
        {contract.code}
      </Badge>
    </div>

    <div className="min-w-0 text-sm text-slate-900 font-medium truncate" title={contract.title}>
      {contract.title || "—"}
    </div>

    <div className="min-w-0 text-sm text-slate-700 truncate" title={contract.client_name}>
      {contract.client_name || "—"}
      {contract.partner_name && (
        <div className="text-xs text-slate-500 truncate" title={contract.partner_name}>{contract.partner_name}</div>
      )}
    </div>

    <div className="text-sm text-slate-600 tabular-nums whitespace-nowrap">
      {contract.start_date_display || "—"}
      {contract.end_date_display && (
        <>
          <span className="mx-1.5 text-slate-300">→</span>
          {contract.end_date_display}
        </>
      )}
    </div>

    <div>
      <Badge tone={STATUS_TONE[contract.status] || "neutral"} variant="soft" size="xs">
        {contract.status_label}
      </Badge>
    </div>
  </div>
)

const HeaderRow = ({ order, direction, onSortChange }) => (
  <div
    role      = "row"
    className = "grid items-center gap-x-3 px-4 py-3 border-b border-slate-200 bg-slate-50/50"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
  >
    <SortHeader label={I18n.t("mbc_contracts.csv.contract_code",       { defaultValue: "Référence" })} column="mbc_contracts.contract_code"       order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("mbc_contracts.csv.contract_title",      { defaultValue: "Titre" })}     column="mbc_contracts.title"               order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("navigation.client",                     { defaultValue: "Client" })}    column="clients.name"                       order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("maintenance.operations_start_date",     { defaultValue: "Période" })}   column="mbc_contracts.start_date_contract" order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("mbc_contracts.status.label",            { defaultValue: "Statut" })}    column="mbc_contracts.status"              order={order} direction={direction} onSortChange={onSortChange} />
  </div>
)

const MbcContractsTable = ({ mbcContracts, order, direction, onSortChange }) => (
  <div className="overflow-x-auto" role="table">
    <HeaderRow order={order} direction={direction} onSortChange={onSortChange} />
    {mbcContracts.map(contract => (
      <ContractRow key={contract.id} contract={contract} />
    ))}
    {mbcContracts.length === 0 && (
      <EmptyState size="lg">
        {I18n.t("mbc_contracts.none", { defaultValue: "Aucun marché à bons de commande" })}
      </EmptyState>
    )}
  </div>
)

export default MbcContractsTable
